import React from 'react';
import * as ct from 'countries-and-timezones';
import * as Yup from 'yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import { MuiForm } from 'lib/Form';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';

export interface LocationGeneralFormData {
    title: string;
    description: string;
    phone: string;
    email: string;
    VATnumber: string;
    timezone: string;
    state: string;
}

export const generalDefaults: LocationGeneralFormData = {
    title: '',
    description: '',
    phone: '',
    email: '',
    VATnumber: '',
    timezone: 'Europe/London',
    state: 'PENDING'
};

const TIMEZONE_OPTIONS = Object.entries(ct.getAllTimezones()).map(([key]) => ({ label: key, value: key }));

const STATE_OPTIONS = [
    { label: 'ACTIVE', value: 'ACTIVE' },
    { label: 'RETIRED', value: 'RETIRED' },
    { label: 'PENDING', value: 'PENDING' }
];

const validationSchema = Yup.object({
    title: Yup.string().required('Title is required').min(3, 'Must be at least 3 characters'),
    email: Yup.string().email('Invalid email address'),
    timezone: Yup.string().required('Timezone is required')
});

interface LocationGeneralFormProps {
    open: boolean;
    onSubmit: (data: LocationGeneralFormData) => void;
    initialValues: LocationGeneralFormData;
}

export const LocationGeneralForm: React.FC<LocationGeneralFormProps> = ({
    open,
    onSubmit,
    initialValues
}) => {
    if (!open) {
        return null;
    }
    return (
        <MuiForm onSubmit={onSubmit} validationSchema={validationSchema} initialValues={initialValues}>
            {({ submitForm }) => (
                <Form>
                    <Box width="100%" sx={{ px: 3, pt: 1 }}>
                        <Typography variant="body2">
                            General information about your location including contact information.
                        </Typography>
                    </Box>
                    <Box padding={3}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <TextFormField
                                    name="title"
                                    label={<MuiFormLabel required>Title</MuiFormLabel>}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="description" label="Description" multiline minRows={2} />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="phone" label="Phone Number" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="email" label="Email Address" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="VATnumber" label="VAT Number" />
                            </Grid>
                            <Grid size={12}>
                                <AutocompleteFormField
                                    name="timezone"
                                    label={<MuiFormLabel required>Timezone</MuiFormLabel>}
                                    options={TIMEZONE_OPTIONS}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SelectFormField name="state" label="State" options={STATE_OPTIONS} />
                            </Grid>
                            <Grid size={12}>
                                <Row align="flex-end" gutter>
                                    <Button onClick={submitForm} color="primary" variant="contained">
                                        Next
                                    </Button>
                                </Row>
                            </Grid>
                        </Grid>
                    </Box>
                </Form>
            )}
        </MuiForm>
    );
};
